import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, QrCode as QrCodeIcon, Download, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import { adminPromotersAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import { API_BASE_URL } from '../../config';

const PromotersManagement = () => {
  const [promoters, setPromoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedPromoter, setSelectedPromoter] = useState(null);
  const [selectedQrPromoter, setSelectedQrPromoter] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    notes: '',
    is_active: true,
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  // Obtener URL base del frontend
  const getFrontendUrl = () => {
    return window.location.origin;
  };

  // Generar URL con parámetro UTM para el promotor
  const getPromoterUrl = (promoterId) => {
    const frontendUrl = getFrontendUrl();
    return `${frontendUrl}/?utm_source=${promoterId}`;
  };

  useEffect(() => {
    loadPromoters();
  }, [searchTerm]);

  const loadPromoters = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 15,
        ...(searchTerm && { search: searchTerm }),
      };
      const response = await adminPromotersAPI.getAll(params);
      setPromoters(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar los promotores",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (promoter = null) => {
    if (promoter) {
      setSelectedPromoter(promoter);
      setFormData({
        name: promoter.name || '',
        notes: promoter.notes || '',
        is_active: promoter.is_active ?? true,
      });
    } else {
      setSelectedPromoter(null);
      setFormData({
        name: '',
        notes: '',
        is_active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPromoter(null);
    setFormData({
      name: '',
      notes: '',
      is_active: true,
    });
  };

  const handleShowQr = (promoter) => {
    setSelectedQrPromoter(promoter);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = () => {
    setQrModalOpen(false);
    setSelectedQrPromoter(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedPromoter) {
        await adminPromotersAPI.update(selectedPromoter.id, formData);
        toast({
          title: "Éxito",
          description: "Promotor actualizado exitosamente",
        });
      } else {
        await adminPromotersAPI.create(formData);
        toast({
          title: "Éxito",
          description: "Promotor creado exitosamente",
        });
      }
      handleCloseModal();
      loadPromoters(pagination.current_page);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el promotor",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este promotor?')) {
      return;
    }
    try {
      await adminPromotersAPI.delete(id);
      toast({
        title: "Éxito",
        description: "Promotor eliminado exitosamente",
      });
      loadPromoters(pagination.current_page);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el promotor",
        variant: "destructive",
      });
    }
  };

  const handleCopyUrl = (promoterId) => {
    const url = getPromoterUrl(promoterId);
    navigator.clipboard.writeText(url);
    toast({
      title: "Copiado",
      description: "URL copiada al portapapeles",
    });
  };

  const handleDownloadQr = (promoterId) => {
    const canvas = document.getElementById(`qr-${promoterId}`);
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `promoter-${promoterId}-qr.png`;
      link.href = url;
      link.click();
    }
  };

  return (
    <div className="p-6">
      <Toaster />
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Gestión de Promotores</h2>
          <p className="text-[#A2A2A2]">Administra los promotores y genera sus códigos QR</p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-white text-black hover:bg-white/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Promotor
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A2A2A2] w-4 h-4" />
          <Input
            type="text"
            placeholder="Buscar promotores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/5 border-white/10 text-white"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-[#A2A2A2]">
                    Cargando...
                  </td>
                </tr>
              ) : promoters.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-[#A2A2A2]">
                    No hay promotores registrados
                  </td>
                </tr>
              ) : (
                promoters.map((promoter) => (
                  <tr key={promoter.id} className="hover:bg-white/5">
                    <td className="px-6 py-4 whitespace-nowrap text-white">
                      {promoter.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-white">
                      {promoter.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          promoter.is_active
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}
                      >
                        {promoter.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleShowQr(promoter)}
                          className="text-white hover:bg-white/10"
                          title="Ver QR"
                        >
                          <QrCodeIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenModal(promoter)}
                          className="text-white hover:bg-white/10"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {/* <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(promoter.id)}
                          className="text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => loadPromoters(pagination.current_page - 1)}
            disabled={pagination.current_page === 1}
            className="border-white/10 text-white"
          >
            Anterior
          </Button>
          <span className="px-4 py-2 text-white">
            Página {pagination.current_page} de {pagination.last_page}
          </span>
          <Button
            variant="outline"
            onClick={() => loadPromoters(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.last_page}
            className="border-white/10 text-white"
          >
            Siguiente
          </Button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedPromoter ? 'Editar Promotor' : 'Nuevo Promotor'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-white">
                Nombre *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="notes" className="text-white">
                Notas
              </Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-[#A2A2A2] focus:outline-none focus:ring-2 focus:ring-white/20"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active" className="text-white cursor-pointer">
                Activo
              </Label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
                className="border-white/10 text-white"
              >
                Cancelar
              </Button>
              <Button type="submit" className="bg-white text-black hover:bg-white/90">
                {selectedPromoter ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Código QR - {selectedQrPromoter?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedQrPromoter && (
            <div className="space-y-4">
              <div className="flex justify-center bg-white p-4 rounded-lg" data-qr-svg>
                <QRCodeSVG
                  value={getPromoterUrl(selectedQrPromoter.id)}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white text-sm">URL del Promotor</Label>
                <div className="flex gap-2">
                  <Input
                    value={getPromoterUrl(selectedQrPromoter.id)}
                    readOnly
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyUrl(selectedQrPromoter.id)}
                    className="border-white/10 text-white"
                    title="Copiar URL"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Obtener el SVG del QR
                    const svgElement = document.querySelector('[data-qr-svg] svg');
                    if (svgElement) {
                      const svgData = new XMLSerializer().serializeToString(svgElement);
                      const canvas = document.createElement('canvas');
                      const ctx = canvas.getContext('2d');
                      const img = new Image();
                      img.onload = () => {
                        canvas.width = img.width;
                        canvas.height = img.height;
                        ctx.drawImage(img, 0, 0);
                        const url = canvas.toDataURL('image/png');
                        const link = document.createElement('a');
                        link.download = `promoter-${selectedQrPromoter.id}-qr.png`;
                        link.href = url;
                        link.click();
                      };
                      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                    }
                  }}
                  className="border-white/10 text-white flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar QR
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCloseQrModal}
                  className="border-white/10 text-white"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromotersManagement;

